(() => {
  if (customElements.get('nfd-nyc-studio')) return;
  const array = value => {
    if (Array.isArray(value)) return value;
    try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  };
  const table = (columns, rows, label) => {
    columns = array(columns);
    rows = array(rows);
    if (columns.length < 2 || !rows.some(row => Array.isArray(row?.values) && row.values.some(value => value != null && String(value).trim() !== '' && Number.isFinite(Number(value))))) return null;
    const element = document.createElement('table');
    const caption = element.createCaption();
    caption.textContent = label;
    const head = element.createTHead().insertRow();
    columns.forEach(value => {
      const cell = document.createElement('th');
      cell.scope = 'col'; cell.textContent = value ?? ''; head.append(cell);
    });
    const body = element.createTBody();
    rows.forEach(row => {
      if (!row || !Array.isArray(row.values)) return;
      const line = body.insertRow();
      const header = document.createElement('th');
      header.scope = 'row'; header.textContent = row.label ?? ''; line.append(header);
      columns.slice(1).forEach((_, index) => {
        const cell = line.insertCell(); cell.textContent = row.values[index] ?? '—';
        const value = row.values[index];
        if (value != null && String(value).trim() !== '' && Number.isFinite(Number(value))) cell.dataset.inches = String(Number(value));
      });
    });
    return element;
  };
  customElements.define('nfd-nyc-studio', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const blockUnavailable = event => {
        const target = event.target;
        if (!this.unavailableVariant() || !(target instanceof Element)) return;
        const purchase = event.type === 'submit' ? target.matches('.product-details form[action*="/cart/add"]') : target.closest('.product-details button[name="add"]');
        if (!purchase) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.guardStock();
      };
      this.addEventListener('click', blockUnavailable, { capture: true, signal });
      this.addEventListener('submit', blockUnavailable, { capture: true, signal });
      this.desktop = window.matchMedia?.('(min-width: 1000px)') || { matches: false };
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-studio-tabs] [role="tab"]');
        if (tab) this.activateTab(tab);
      }, { signal });
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-studio-tabs] [role="tab"]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const tabs = [...tab.parentElement.querySelectorAll('[role="tab"]')].filter(item => !item.hidden);
        let index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        event.preventDefault(); this.activateTab(tabs[index], true);
      }, { signal });
      this.desktop.addEventListener?.('change', () => this.refresh(), { signal });
      this.refresh();
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'data-coming-soon', 'data-studio-single-available'] });
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.measureCard());
        this.observeCard();
      }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); this.resizeObserver?.disconnect(); this.observedCard = null;
    }
    observeCard() {
      const card = this.querySelector('.nfd-studio__buycard');
      if (this.observedCard === card) return;
      this.resizeObserver?.disconnect(); this.observedCard = card;
      if (card) this.resizeObserver?.observe(card);
      this.measureCard();
    }
    measureCard() {
      const height = this.querySelector('.nfd-studio__buycard')?.getBoundingClientRect().height;
      if (height) this.style.setProperty('--studio-card-height', `${height}px`);
    }
    unavailableVariant() {
      const details = this.querySelector('.product-details');
      if (details?.querySelector('[data-nfd-stock-state]')?.dataset.comingSoon === 'true') return true;
      const json = details?.querySelector('variant-picker script[type="application/json"]');
      if (!json) return this.querySelector('.nfd-studio__buycard')?.dataset.studioSingleAvailable === 'false';
      try { const variant = JSON.parse(json.textContent); return variant === null || variant?.available === false; }
      catch { return false; }
    }
    guardStock() {
      if (!this.unavailableVariant()) return;
      this.querySelectorAll('.product-details button[name="add"]').forEach(button => {
        if (!button.disabled) button.disabled = true;
      });
    }
    activateTab(tab, focus = false) {
      const information = tab?.closest('.nfd-studio__information');
      if (!information || tab.hidden) return;
      information.querySelectorAll('[data-studio-tabs] [role="tab"]').forEach(item => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected)); item.tabIndex = selected ? 0 : -1;
        const panel = [...information.querySelectorAll('[data-studio-panel]')].find(node => node.id === item.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tab.focus();
    }
    refresh() {
      if (!this.isConnected) return;
      this.guardStock();
      this.renderCharts();
      const information = this.querySelector('.nfd-studio__information');
      if (!information) return;
      const fabric = information.querySelector('[data-studio-panel="fabric"]');
      const fitPanel = information.querySelector('[data-studio-panel="fit"]');
      const legacy = information.querySelector('[data-studio-legacy-information]');
      if (legacy && !legacy.dataset.prepared) {
        const description = legacy.querySelector('.nfd-studio-description');
        if (description) {
          const accordion = description.closest('accordion-custom');
          fabric.prepend(description); accordion?.remove();
        }
        const policy = legacy.querySelector('.popup-link__inner');
        if (policy) {
          const component = policy.closest('dialog-component');
          information.querySelector('[data-studio-policy]')?.append(...policy.childNodes);
          component?.remove();
        }
        legacy.dataset.prepared = 'true';
      }
      const description = information.querySelector('.nfd-studio-description');
      const specifications = information.querySelector('[data-studio-specifications]');
      if (description && this.description !== description) {
        this.description = description;
        this.specifications = [...description.querySelectorAll('li')].filter(item => !item.parentElement.closest('li')).map(item => {
          const anchor = document.createComment('NYC specification position'); item.before(anchor); return { item, anchor };
        });
        const intro = information.querySelector('[data-studio-intro]');
        const normalize = text => text?.replace(/\s+/g, ' ').trim();
        const opening = [...description.querySelectorAll('p, div')].find(node => !node.children.length && normalize(node.textContent) === normalize(intro?.textContent));
        if (opening && intro) opening.hidden = true;
        else if (intro) intro.hidden = true; // Keep linked/formatted opening paragraphs whole.
      }
      if (specifications) {
        for (const { item, anchor } of this.specifications || []) {
          if (this.desktop.matches) { if (item.parentElement !== specifications) specifications.append(item); }
          else if (item.previousSibling !== anchor) anchor.after(item);
        }
        specifications.closest('aside').hidden = !this.desktop.matches || !specifications.children.length;
      }
      const fit = information.querySelector('.nfd-studio__fit');
      const fitHome = information.querySelector('.nfd-studio__fit-home');
      const fitTarget = this.desktop.matches ? fitHome : fitPanel;
      if (fit && fitTarget && fit.parentElement !== fitTarget) fitTarget.prepend(fit);
      const tablist = information.querySelector('[data-studio-tabs]');
      if (tablist) {
        const tabs = [...tablist.querySelectorAll('[role="tab"]')];
        for (const tab of tabs) {
          const panel = [...information.querySelectorAll('[data-studio-panel]')].find(node => node.id === tab.getAttribute('aria-controls'));
          const copy = panel?.cloneNode(true);
          copy?.querySelectorAll('[hidden], .nfd-studio__panel-heading').forEach(node => node.remove());
          tab.hidden = !copy || !(copy.textContent.trim() || copy.querySelector('img, table, a, button'));
        }
        const active = tabs.find(tab => !tab.hidden && tab.getAttribute('aria-selected') === 'true') || tabs.find(tab => !tab.hidden);
        tablist.hidden = !active; information.dataset.tabsReady = 'true';
        if (active) this.activateTab(active);
      }
      this.observeCard();
    }
    renderCharts() {
      this.querySelectorAll('[data-studio-chart]').forEach(mount => {
        if (mount.dataset.ready) return;
        let data;
        try { data = JSON.parse(mount.querySelector('[data-studio-chart-data]').getAttribute('data-studio-chart-data')); } catch { return; }
        const before = table(data.columns, data.rows, data.beforeLabel);
        const after = table(data.postColumns, data.postRows, data.afterLabel);
        if (!before && !after) return;
        mount.querySelector('[data-studio-chart-fallback]')?.remove();
        if (before) mount.append(before);
        if (after) mount.append(after);
        const units = document.createElement('select'); units.setAttribute('aria-label', data.unitLabel || 'Units');
        [['in', data.inchesLabel || 'Inches'], ['cm', data.centimetresLabel || 'Centimetres']].forEach(([value, text]) => { const option = document.createElement('option'); option.value = value; option.textContent = text; units.append(option); });
        units.addEventListener('change', () => {
          mount.querySelectorAll('[data-inches]').forEach(cell => { cell.textContent = String(Math.round(Number(cell.dataset.inches) * (units.value === 'cm' ? 2.54 : 1) * 100) / 100); });
          const note = mount.closest('details')?.querySelector('[data-studio-unit-note]');
          if (note) note.textContent = units.value === 'cm' ? data.centimetresNote : data.inchesNote;
        });
        mount.prepend(units);
        mount.dataset.ready = 'true';
      });
    }
  });
})();
